// Package Imports
import { searchProductMapper } from '@/lib/mappers/searchProductMapper';
import { defaultStoreNumber } from '@/lib/constants/store';

// Type Imports
import { SearchResultsResponse } from '@/lib/types/search';
import { SearchProductSylius } from '@/lib/types/searchProductSylius';
import { PriceRangeValues } from '@/components/search/searchFilters';

/**
 * Fetches product search results from the Sylius API using provided filters.
 *
 * This function supports:
 * - Keyword-based search (baseQuery and userQuery)
 * - Sorting and pagination
 * - Store-specific results via storeNumber
 * - Faceted filtering (including nested price range)
 *
 * Results are dynamically revalidated based on CACHE_INVALIDATION_TIME.
 * All results are mapped through `searchProductMapper` for consistency.
 */
export async function fetchSyliusSearch({
  baseQuery,
  userQuery,
  orderBy,
  page = '1',
  pageSize = '16',
  storeNumber = String(defaultStoreNumber),
  facets = {},
  priceRange,
}: {
  baseQuery?: string | string[];
  userQuery?: string;
  orderBy?: string;
  page?: string;
  pageSize?: string;
  storeNumber?: string;
  facets: Record<string, string | string[]>;
  priceRange?: PriceRangeValues;
}): Promise<SearchResultsResponse> {
  const channel      = process.env.NEXT_PUBLIC_SYLIUS_CHANNEL_CODE || 'SPECS';
  const syliusApiUrl = process.env.NEXT_PUBLIC_SYLIUS_API;

  const params = new URLSearchParams();

  // Core params
  params.set('type', 'keyword');
  params.set('channelCode', channel);
  params.set('storeNumber', storeNumber);
  params.set('page', page);
  params.set('itemsPerPage', pageSize);

  // Handle array input for baseQuery
  if (Array.isArray(baseQuery)) {
    baseQuery = JSON.stringify(baseQuery);
  }

  if (baseQuery) params.set('baseQuery', baseQuery);
  if (userQuery) params.set('userQuery', userQuery);

  // Set sort field (only if not default values)
  if (orderBy && !['relevancy', 'popularity'].includes(orderBy)) {
    params.set('sort', orderBy);
  }

  // Add all non-price facets
  for (const [key, value] of Object.entries(facets)) {
    if (key === 'price_range') continue;

    if (Array.isArray(value)) {
      params.set(`facets[${key}]`, JSON.stringify(value));
    } else {
      params.set(`facets[${key}]`, value);
    }
  }

  // Handle price range separately
  if (priceRange) {
    if (priceRange.min_price !== undefined) {
      params.set('facets[price_range][min_price]', String(priceRange.min_price));
    }
    if (priceRange.max_price !== undefined) {
      params.set('facets[price_range][max_price]', String(priceRange.max_price));
    }
  }

  // Construct the full API URL
  const url = `${syliusApiUrl}/api/v2/shop/search?${params.toString()}`;

  // Fetch results from Sylius
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    cache: 'no-cache',
    next: {
      revalidate: Number(process.env.CACHE_INVALIDATION_TIME) || 0,
    },
  });

  if (!res.ok) {
    console.error(`[Sylius Search Error] ${res.status}: ${res.statusText}`);
    return { message: 'Search failed', status: res.status };
  }

  let rawResults: Record<string, unknown>;
  try {
    const parsed: unknown = await res.json();
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { message: 'Invalid search response', status: res.status };
    }
    rawResults = parsed as Record<string, unknown>;
  } catch {
    console.error(`[Sylius Search Error] Invalid JSON body (status ${res.status})`);
    return { message: 'Invalid search response', status: res.status };
  }

  const products = Array.isArray(rawResults.products) ? rawResults.products as SearchProductSylius[] : [];
  const mappedResults = products.map((item) => searchProductMapper(item));

  return {
    ...rawResults,
    products: mappedResults,
  } as SearchResultsResponse;
}
